import { Avatar, Box, Button, Card, CardActions, CardContent, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";


export interface ProfileAvatarProps {
  // record: UserResponseDto;
  prevAvatarUrl: string | null | undefined;
  username: string | null | undefined;
  setAvatarFile: (x?: File) => void;
}


const ProfileAvatar: FC<ProfileAvatarProps> = ({
  prevAvatarUrl,
  username,
  setAvatarFile,
}) => {

  const { enqueueSnackbar } = useSnackbar()
  const [url, setUrl] = useState('')
  const urlRef = useRef(url)
  urlRef.current = url
  
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    maxSize: 3145728, // 3MB
    multiple: false,
    onDropAccepted(acceptedFiles: File[]) {
      if (acceptedFiles.length > 0) {
        const newFile = acceptedFiles[0]
        const supportedImgTypes = ['.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp']
        let unsupportedImgType = !newFile.type.startsWith('image/')
        if (!unsupportedImgType) {
          const iDot = newFile.name.lastIndexOf('.')
          if (iDot !== -1) {
            const extension = newFile.name.substring(iDot)
            unsupportedImgType = supportedImgTypes.indexOf(extension.toLowerCase()) < 0
          }
        }
        if (unsupportedImgType) {
          enqueueSnackbar('Unsupported image file type. The supported file extensions are: .png/.jpg/.jpeg/.tiff/.tif/.bmp', { variant: 'warning' })
          return
        }

        setAvatarFile(newFile)
        if (url && url !== prevAvatarUrl) {
          const prevUrl = url
          setTimeout(() => URL.revokeObjectURL(prevUrl))
        }
        setUrl(URL.createObjectURL(newFile))
      }
    },
    onDropRejected(fileRejections) {
      if (fileRejections.length > 0) {
        const failure = fileRejections[0].errors[0]
        enqueueSnackbar(
            failure.code === 'file-too-large'    ? 'Can not upload an image larger than 3MB' 
          : failure.code === 'file-invalid-type' ? 'Unsupported image file type. The supported file extensions are: .png/.jpg/.jpeg/.tiff/.tif/.bmp'
          : failure.message, 
          { variant: 'error' }
        )
      }
    },
  });

  useEffect(() => {
    if (prevAvatarUrl) {
      setUrl(prevUrl => {
        prevUrl && setTimeout(() => URL.revokeObjectURL(prevUrl))
        return ''
      })
    }
  }, [prevAvatarUrl])

  useEffect(() => {
    return () => {
      urlRef.current && URL.revokeObjectURL(urlRef.current)
    }
  }, [])

  const resetPicture = () => {
    if (url !== prevAvatarUrl) {
      setUrl(prevAvatarUrl || '')
      setAvatarFile(undefined)
    }
  }


  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              p: 1,
              border: (theme) => `1px dashed ${theme.palette.divider}`,
              borderRadius: '50%',
              position: 'relative',
            }}
          >
            <Avatar
              alt={username || 'user avatar picture'}
              src={url || prevAvatarUrl || ''}
              sx={{
                height: 100,
                width: 100
              }}
            />
            <Box
              role="button"
              aria-label="Change User Avatar"
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: '100%',
                borderRadius: '50%',
                cursor: 'pointer',
              }}
              {...getRootProps()}
            >
              <input id="choose-user-avatar-file" aria-label="User Avatar"
                {...getInputProps()}
              />
            </Box>
          </Box>
          <Typography
            color="textPrimary"
            sx={{ mt: 1, lineBreak: 'anywhere' }}
            variant="subtitle2"
          >
            {username}
            {/* {user?.username} */}
          </Typography>
        </Box>
      </CardContent>
      {(!url || !url.startsWith('blob:')) ? null :
        <CardActions>
          <Button
            color="primary"
            fullWidth
            variant="text"
            onClick={resetPicture}
          >
            Reset Picture
          </Button>
        </CardActions>}
    </Card>
  )
}

export default ProfileAvatar;
